import { API_AGING_REPORTS } from "../../../../utils/apis";
import {
  beautify,
  NoWrap,
  numberFormat,
  returnObject,
} from "../../../../utils/helpers";

type TAgingReportProps = {
  dateFilterKey?: string;
  status?: string;
  noWrapColumns?: string[];
  numberColumns?: string[];
};

const useAgingReport = ({
  dateFilterKey,
  noWrapColumns = [],
  numberColumns = [],
}: TAgingReportProps) => {
  const tableColumns = (data: any) => {
    const firstItem = data[0] || {};

    const filterColumns = [
      {
        id: "ticket_number",
        header: <NoWrap>Ticket Number</NoWrap>,
        filter: { type: "text" },
      },

      {
        id: "district",
        header: <NoWrap>District</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "street_name",
        header: <NoWrap>Street Name</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "number_plate",
        header: <NoWrap>Number Plate</NoWrap>,
        filter: { type: "text" },
      },
      {
        id: "assessed_amount",
        header: <NoWrap>Assessed Amount</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "penalty_charged",
        header: <NoWrap>Penalty Charged</NoWrap>,
        filter: { type: "number" },
      },

      {
        id: "days_past_due",
        header: <NoWrap>Days Past Due</NoWrap>,
        filter: { type: "number" },
      },
      {
        id: "total_outstanding_amount",
        header: <NoWrap>Outstanding Amount</NoWrap>,
        filter: { type: "number" },
      },
    ];

    // Generate columns from data structure
    const columns = Object.keys(firstItem).map((column: any) => ({
      id: column,
      header: <NoWrap>{beautify(column)}</NoWrap>,
      filter: [
        "ticket_number",
        "street_name",
        "number_plate",
        "assessed_amount",
        "penalty_charged",
        "total_outstanding_amount",
        "days_past_due",
      ].includes(column)
        ? false
        : true,
    }));

    return [...filterColumns, ...columns];
  };

  const tableData = (data: any) => {
    return data?.data?.map((item: any) => ({
      ...item,
      [dateFilterKey || "created_at"]: (
        <NoWrap>{item[dateFilterKey || "created_at"]}</NoWrap>
      ),
      ticket_number: <NoWrap>{item.ticket_number || "N/A"}</NoWrap>,
      number_plate: (
        <span
          className={`${
            item.number_plate === "GRAND TOTAL"
              ? "font-bold text-blue-800 bg-blue-50 px-2 py-1 rounded"
              : ""
          }`}
        >
          <NoWrap>{item.number_plate}</NoWrap>
        </span>
      ),
      assessed_amount: (
        <span className="font-medium text-gray-900">
          {numberFormat(item.assessed_amount)}
        </span>
      ),
      penalty_charged: (
        <span className="font-medium text-orange-600">
          {numberFormat(item.penalty_charged)}
        </span>
      ),
      total_arrears: (
        <span className="font-medium text-purple-600">
          {numberFormat(item.total_arrears)}
        </span>
      ),
      total_outstanding_amount: (
        <span
          className={`font-medium ${
            item.total_outstanding_amount > 0
              ? "text-red-600"
              : "text-green-600"
          }`}
        >
          {numberFormat(item.total_outstanding_amount)}
        </span>
      ),
      days_past_due:
        item.days_past_due !== "" ? (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              item.days_past_due <= 7
                ? "bg-yellow-100 text-yellow-800"
                : item.days_past_due <= 14
                  ? "bg-orange-100 text-orange-800"
                  : "bg-red-100 text-red-800"
            }`}
          >
            {item.days_past_due} days
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
      aging_category:
        item.days_past_due !== "" ? (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${
              item.days_past_due <= 7
                ? "bg-green-100 text-green-800"
                : item.days_past_due <= 14
                  ? "bg-yellow-100 text-yellow-800"
                  : item.days_past_due <= 21
                    ? "bg-orange-100 text-orange-800"
                    : "bg-red-100 text-red-800"
            }`}
          >
            {item.days_past_due <= 7
              ? "Current"
              : item.days_past_due <= 14
                ? "1-2 Weeks"
                : item.days_past_due <= 21
                  ? "2-3 Weeks"
                  : "Over 3 Weeks"}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
      ...returnObject(
        noWrapColumns.length > 0,
        noWrapColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = <NoWrap>{item[column]}</NoWrap>;
          }
          return obj;
        }, {}),
      ),
      ...returnObject(
        numberColumns.length > 0,
        numberColumns.reduce((obj, column) => {
          if (item[column] !== undefined) {
            obj[column] = numberFormat(item[column]);
          }
          return obj;
        }, {}),
      ),
    }));
  };

  const initialFilters = {
    ticket_number: "",
    district: "",
    number_plate: "",
    assessed_amount: "",
    penalty_charged: "",
    total_outstanding_amount: "",
    days_past_due: "",
  };

  const getAgingStats = (data: any) => {
    const filteredData = data.filter(
      (item: any) => item.number_plate !== "GRAND TOTAL",
    );
    const grandTotalRow = data.find(
      (item: any) => item.number_plate === "GRAND TOTAL",
    );

    const totalAssessed = grandTotalRow?.assessed_amount || 0;
    const totalPenalties = grandTotalRow?.penalty_charged || 0;
    const totalArrears = grandTotalRow?.total_arrears || 0;
    const totalOutstanding = grandTotalRow?.total_outstanding_amount || 0;
    const totalTickets = filteredData.length;

    // Calculate average days past due
    const validDaysPastDue = filteredData.filter(
      (item: any) => item.days_past_due !== "" && !isNaN(item.days_past_due),
    );
    const averageDaysPastDue =
      validDaysPastDue.length > 0
        ? validDaysPastDue.reduce(
            (sum: number, item: any) => sum + item.days_past_due,
            0,
          ) / validDaysPastDue.length
        : 0;

    return {
      totalTickets,
      totalAssessed,
      totalPenalties,
      totalArrears,
      totalOutstanding,
      averageDaysPastDue: averageDaysPastDue.toFixed(1),
      penaltyRate:
        totalAssessed > 0
          ? ((totalPenalties / totalAssessed) * 100).toFixed(1)
          : "0.0",
      collectionsDeficit: totalArrears - totalOutstanding,
    };
  };

  const getAgingBuckets = (data: any) => {
    const filteredData = data.filter(
      (item: any) =>
        item.number_plate !== "GRAND TOTAL" && item.days_past_due !== "",
    );

    const buckets = {
      current: { count: 0, amount: 0, label: "0-7 days" },
      week1to2: { count: 0, amount: 0, label: "8-14 days" },
      week2to3: { count: 0, amount: 0, label: "15-21 days" },
      overThreeWeeks: { count: 0, amount: 0, label: "Over 21 days" },
    };

    filteredData.forEach((item: any) => {
      const days = item.days_past_due;
      const amount = item.total_outstanding_amount || 0;

      if (days <= 7) {
        buckets.current.count++;
        buckets.current.amount += amount;
      } else if (days <= 14) {
        buckets.week1to2.count++;
        buckets.week1to2.amount += amount;
      } else if (days <= 21) {
        buckets.week2to3.count++;
        buckets.week2to3.amount += amount;
      } else {
        buckets.overThreeWeeks.count++;
        buckets.overThreeWeeks.amount += amount;
      }
    });

    return Object.entries(buckets).map(([key, bucket]) => ({
      category: key,
      label: bucket.label,
      count: bucket.count,
      amount: bucket.amount,
      percentage:
        filteredData.length > 0
          ? ((bucket.count / filteredData.length) * 100).toFixed(1)
          : "0.0",
    }));
  };

  const getVehicleAnalysis = (data: any) => {
    const filteredData = data.filter(
      (item: any) => item.number_plate !== "GRAND TOTAL",
    );

    // Group by number plate
    const vehicleGroups = filteredData.reduce((acc: any, item: any) => {
      const plate = item.number_plate || "Unknown";
      if (!acc[plate]) {
        acc[plate] = {
          ticket_count: 0,
          total_assessed: 0,
          total_penalties: 0,
          total_outstanding: 0,
          max_days_past_due: 0,
          tickets: [],
        };
      }

      acc[plate].ticket_count++;
      acc[plate].total_assessed += item.assessed_amount || 0;
      acc[plate].total_penalties += item.penalty_charged || 0;
      acc[plate].total_outstanding += item.total_outstanding_amount || 0;
      acc[plate].max_days_past_due = Math.max(
        acc[plate].max_days_past_due,
        item.days_past_due || 0,
      );
      acc[plate].tickets.push(item.ticket_number);

      return acc;
    }, {});

    return Object.entries(vehicleGroups)
      .map(([plate, stats]: [string, any]) => ({
        number_plate: plate,
        ...stats,
        average_ticket_value:
          stats.ticket_count > 0
            ? (stats.total_assessed / stats.ticket_count).toFixed(0)
            : "0",
        risk_category:
          stats.max_days_past_due > 21
            ? "High Risk"
            : stats.max_days_past_due > 14
              ? "Medium Risk"
              : "Low Risk",
      }))
      .sort((a: any, b: any) => b.total_outstanding - a.total_outstanding);
  };

  const getTopDelinquents = (data: any, limit: number = 10) => {
    return data
      .filter(
        (item: any) =>
          item.number_plate !== "GRAND TOTAL" &&
          item.total_outstanding_amount > 0,
      )
      .sort(
        (a: any, b: any) =>
          b.total_outstanding_amount - a.total_outstanding_amount,
      )
      .slice(0, limit)
      .map((item: any, index: number) => ({
        rank: index + 1,
        ...item,
        risk_level:
          item.days_past_due > 21
            ? "Critical"
            : item.days_past_due > 14
              ? "High"
              : "Medium",
      }));
  };

  const exportData = {
    api: API_AGING_REPORTS,
    fileName: `aging_${new Date().toISOString().split("T")[0]}.xlsx`,
    showCustomDateRange: true,
    start_date: "start_date",
    end_date: "end_date",
  };

  return {
    tableColumns,
    tableData,
    initialFilters,
    getAgingStats,
    getAgingBuckets,
    getVehicleAnalysis,
    getTopDelinquents,
    exportData,
  };
};

export default useAgingReport;
